import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Clock, Check } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import { getArticle, ARTICLES } from "@/data/articles";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);

  if (!article) {
    return (
      <>
        <Header />
        <main className="bg-sand pb-32 pt-44">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <h1
              className="font-serif text-4xl tracking-tight text-ink"
              data-testid="article-not-found"
            >
              Artikel nicht gefunden
            </h1>
            <Link
              to="/ratgeber"
              data-testid="article-back-link"
              className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-clay"
            >
              <ArrowLeft className="h-4 w-4" /> Zum Ratgeber
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const others = ARTICLES.filter((a) => a.slug !== slug);

  return (
    <>
      <Seo
        title={article.title}
        description={article.description}
        type="article"
        jsonLd={[
          {
            "@type": "BlogPosting",
            headline: article.title,
            description: article.description,
            inLanguage: "de",
            datePublished: article.date,
            author: {
              "@type": "Organization",
              name: "Florida Business & Relocation",
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Startseite", item: "https://auswandernflorida.com/" },
              { "@type": "ListItem", position: 2, name: "Ratgeber", item: "https://auswandernflorida.com/ratgeber" },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]}
      />
      <Header />
      <main>
        <article
          data-testid="article-page"
          className="bg-sand pb-24 pt-36 lg:pt-44"
        >
          <div className="mx-auto max-w-3xl px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Link
                to="/ratgeber"
                data-testid="article-back-link"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink-muted transition-colors hover:text-clay"
              >
                <ArrowLeft className="h-4 w-4" /> Ratgeber
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-10 flex items-center gap-4 text-xs uppercase tracking-[0.2em] text-ink-muted"
            >
              <span className="text-clay" data-testid="article-category">
                {article.category}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                {article.readingTime} Lesezeit
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-6 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl"
              data-testid="article-title"
            >
              {article.title}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-8 border-l-2 border-clay pl-6 font-serif text-xl italic leading-relaxed text-ink-muted"
              data-testid="article-lead"
            >
              {article.description}
            </motion.p>

            <div className="mt-16 space-y-16" data-testid="article-body">
              {article.sections.map((section, i) => (
                <motion.section
                  key={section.heading}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6 }}
                  data-testid={`article-section-${i}`}
                >
                  <h2 className="font-serif text-2xl tracking-tight text-ink sm:text-3xl">
                    {section.heading}
                  </h2>
                  {section.paragraphs?.map((p, j) => (
                    <p
                      key={j}
                      className="mt-5 text-base leading-relaxed text-ink-muted"
                    >
                      {p}
                    </p>
                  ))}
                  {section.list && (
                    <ul className="mt-6 space-y-3">
                      {section.list.map((item, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-3 text-base leading-relaxed text-ink-muted"
                        >
                          <Check className="mt-1 h-4 w-4 shrink-0 text-clay" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.section>
              ))}
            </div>

            <div
              className="mt-24 border border-line bg-sand-deep p-10"
              data-testid="article-cta-box"
            >
              <p className="font-serif text-2xl italic leading-snug tracking-tight text-ink">
                Persönliche Fragen zu Ihrer Auswanderung?
              </p>
              <p className="mt-4 text-sm leading-relaxed text-ink-muted md:text-base">
                Im kostenlosen Erstgespräch klären wir Ihre Situation –
                unverbindlich und auf Deutsch.
              </p>
              <a
                href="/kontakt"
                data-testid="article-cta-button"
                className="group mt-8 inline-flex items-center gap-3 bg-clay px-8 py-4 text-xs uppercase tracking-[0.2em] text-sand transition-colors hover:bg-clay-dark"
              >
                Kostenloses Erstgespräch buchen
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>

            <div className="mt-24 border-t border-line pt-12">
              <p className="text-xs uppercase tracking-[0.3em] text-clay">
                Weiterlesen
              </p>
              <ul className="mt-8 space-y-6" data-testid="article-more-list">
                {others.map((other) => (
                  <li key={other.slug}>
                    <Link
                      to={`/ratgeber/${other.slug}`}
                      data-testid={`article-more-${other.slug}`}
                      className="group flex items-baseline justify-between gap-6"
                    >
                      <span className="font-serif text-xl tracking-tight text-ink transition-colors group-hover:text-clay sm:text-2xl">
                        {other.title}
                      </span>
                      <ArrowRight className="h-4 w-4 shrink-0 text-clay transition-transform group-hover:translate-x-1" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}
